/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  FormHelperText,
  Divider
} from '@mui/material';
import {
  Business,
  Save,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ClientCreationForm from './ClientCreationForm';

// Entity type options
const ENTITY_TYPES = [
  { value: 'CLIENT', label: 'Client' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'PERSON', label: 'Person' },
  { value: 'PROJECT', label: 'Project' },
  { value: 'PROGRAM', label: 'Program' },
  { value: 'EVENT', label: 'Event' },
  { value: 'RESOURCE', label: 'Resource' },
  { value: 'OTHER', label: 'Other' }
];

// Entity status options
const ENTITY_STATUS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ARCHIVED', label: 'Archived' }
];

// eslint-disable-next-line max-lines-per-function
function EntityCreationForm({ onEntityCreated }) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openDialog, setOpenDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    status: 'ACTIVE',
    description: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    tags: [],
    metadata: {},
    // Additional fields for specific entity types
    // PERSON fields - Basic
    firstName: '',
    lastName: '',
    role: '',
    organization: '',
    department: '',
    bio: '',
    skills: [],
    linkedinUrl: '',
    // PERSON fields - Nested Faana structure
    faana: {
      leadership_quality_scores: {
        courage_score: '',
        vulnerability_score: '',
        purpose_driven_score: '',
        learning_agility_score: '',
        determination_score: '',
        self_awareness_score: '',
        adaptability_score: '',
        cross_cultural_score: ''
      },
      framework_scores: {
        holistic_health_score: '',
        financial_fitness_score: '',
        community_engagement_score: '',
        leadership_strength_score: ''
      },
      growth_metrics: {
        growth_trajectory: '',
        growth_velocity: '',
        assessment_date: '',
        coach_name: ''
      },
      risk_assessment: {
        growth_risk_index: '',
        risk_factors: []
      }
    },
    // PROJECT fields
    projectName: '',
    startDate: '',
    endDate: '',
    projectLead: '',
    objectives: '',
    deliverables: [],
    budget: '',
    // PROGRAM fields
    programType: '',
    duration: '',
    targetAudience: '',
    facilitators: [],
    outcomes: [],
    maxParticipants: '',
    // EVENT fields
    eventDate: '',
    eventTime: '',
    location: '',
    eventType: '',
    capacity: '',
    registrationUrl: '',
    agenda: '',
    // RESOURCE fields
    resourceType: '',
    resourceUrl: '',
    accessLevel: 'PUBLIC',
    owner: '',
    version: '',
    lastUpdated: ''
  });

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.length >= 3 ? '' : 'Entity name must be at least 3 characters';
      case 'type':
        return value ? '' : 'Entity type is required';
      case 'email':
        return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email address';
      case 'phone':
        return !value || value.length >= 10 ? '' : 'Invalid phone number';
      case 'website':
        return !value || /^https?:\/\/.+/.test(value) ? '' : 'Website must start with http:// or https://';
      default:
        return '';
    }
  };

  // Handle field changes - supports nested paths
  const handleFieldChange = (name, value) => {
    if (name.includes('.')) {
      // Handle nested field (e.g., 'faana.leadership_quality_scores.courage_score')
      const keys = name.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      // Handle flat field
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setTouchedFields(prev => ({ ...prev, [name]: true }));

    // Validate field
    const error = validateField(name, value);
    setValidationErrors(prev => ({ ...prev, [name]: error }));
  };

  // Get nested field value helper
  const getFieldValue = (name) => {
    if (name.includes('.')) {
      const keys = name.split('.');
      let current = formData;
      for (const key of keys) {
        if (current && typeof current === 'object') {
          current = current[key];
        } else {
          return '';
        }
      }
      return current || '';
    }
    return formData[name] || '';
  };

  // Handle blur event
  const handleFieldBlur = (name) => {
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setValidationErrors(prev => ({ ...prev, [name]: error }));
  };

  // Check if form is valid
  const isFormValid = () => {
    const requiredFields = ['name', 'type'];
    return requiredFields.every(field => {
      const value = formData[field];
      const error = validateField(field, value);
      return value && !error;
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    setOpenDialog(false);
    setIsLoading(true);

    try {
      // Prepare entity data
      const entityData = {
        ...formData,
        created_by: user?.email || '',
        created_by_uid: user?.uid || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Use the API service to create the entity
      const data = await api.createEntity(entityData);

      setSnackbarMessage(`Entity "${data.name}" has been successfully created!`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // Notify parent component
      if (onEntityCreated) {
        onEntityCreated(data);
      }

      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: '',
          type: '',
          status: 'ACTIVE',
          description: '',
          email: '',
          phone: '',
          website: '',
          address: '',
          tags: [],
          metadata: {},
          // Reset PERSON fields
          firstName: '',
          lastName: '',
          role: '',
          organization: '',
          department: '',
          bio: '',
          skills: [],
          linkedinUrl: '',
          faana: {
            leadership_quality_scores: {
              courage_score: '',
              vulnerability_score: '',
              purpose_driven_score: '',
              learning_agility_score: '',
              determination_score: '',
              self_awareness_score: '',
              adaptability_score: '',
              cross_cultural_score: ''
            },
            framework_scores: {
              holistic_health_score: '',
              financial_fitness_score: '',
              community_engagement_score: '',
              leadership_strength_score: ''
            },
            growth_metrics: {
              growth_trajectory: '',
              growth_velocity: '',
              assessment_date: '',
              coach_name: ''
            },
            risk_assessment: {
              growth_risk_index: '',
              risk_factors: []
            }
          }
        });
        setTouchedFields({});
        setValidationErrors({});
      }, 2000);

    } catch (error) {
      console.error('Error during entity creation:', error);
      setSnackbarMessage(error.message || 'An error occurred while creating the entity');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle tag addition
  const handleAddTag = (event) => {
    if (event.key === 'Enter' && event.target.value.trim()) {
      event.preventDefault();
      const newTag = event.target.value.trim();
      if (!formData.tags.includes(newTag)) {
        handleFieldChange('tags', [...formData.tags, newTag]);
      }
      event.target.value = '';
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    handleFieldChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  // If CLIENT type is selected and we have a type, show the ClientCreationForm
  if (formData.type === 'CLIENT') {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleFieldChange('type', '')}
            sx={{ mb: 2 }}
          >
            ← Back to Entity Type Selection
          </Button>
        </Box>
        <ClientCreationForm onClientCreated={(client) => {
          // Convert client creation to entity creation
          if (onEntityCreated) {
            onEntityCreated({
              ...client,
              type: 'CLIENT',
              entityId: client.id || client.clientId
            });
          }
        }} />
      </Box>
    );
  }

  // Function to render type-specific fields
  // eslint-disable-next-line max-lines-per-function
  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'PERSON':
        return (
          <>
            {/* Basic Information */}
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Basic Information
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Role/Title"
                value={formData.role}
                onChange={(e) => handleFieldChange('role', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Organization"
                value={formData.organization}
                onChange={(e) => handleFieldChange('organization', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => handleFieldChange('department', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="LinkedIn URL"
                value={formData.linkedinUrl}
                onChange={(e) => handleFieldChange('linkedinUrl', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Bio"
                value={formData.bio}
                onChange={(e) => handleFieldChange('bio', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
            {/* Leadership Quality Scores */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Leadership Quality Scores (1-3 Scale)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on Faana Leadership Assessment Framework v2.1
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Courage Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.courage_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.courage_score', e.target.value)}
                helperText="Ability to face challenges and take calculated risks (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Vulnerability Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.vulnerability_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.vulnerability_score', e.target.value)}
                helperText="Openness to feedback and admitting weaknesses (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Purpose-Driven Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.purpose_driven_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.purpose_driven_score', e.target.value)}
                helperText="Alignment with mission beyond personal success (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Learning Agility Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.learning_agility_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.learning_agility_score', e.target.value)}
                helperText="Speed of acquiring and applying new knowledge (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Determination Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.determination_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.determination_score', e.target.value)}
                helperText="Persistence in pursuing goals despite obstacles (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Self-Awareness Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.self_awareness_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.self_awareness_score', e.target.value)}
                helperText="Understanding of personal impact on others (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Adaptability Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.adaptability_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.adaptability_score', e.target.value)}
                helperText="Flexibility in changing circumstances (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Cross-Cultural Intelligence Score"
                type="number"
                value={getFieldValue('faana.leadership_quality_scores.cross_cultural_score')}
                onChange={(e) => handleFieldChange('faana.leadership_quality_scores.cross_cultural_score', e.target.value)}
                helperText="Effectiveness across cultural boundaries (1.0-3.0)"
                inputProps={{ min: 1, max: 3, step: 0.1 }}
              />
            </Grid>
            {/* Framework Scores */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Framework Scores (0-100 Scale)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Comprehensive assessment frameworks
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Holistic Health Score"
                type="number"
                value={getFieldValue('faana.framework_scores.holistic_health_score')}
                onChange={(e) => handleFieldChange('faana.framework_scores.holistic_health_score', e.target.value)}
                helperText="Physical, mental, and spiritual wellness (0-100)"
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Financial Fitness Score"
                type="number"
                value={getFieldValue('faana.framework_scores.financial_fitness_score')}
                onChange={(e) => handleFieldChange('faana.framework_scores.financial_fitness_score', e.target.value)}
                helperText="Financial acumen and resource management (0-100)"
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Community Engagement Score"
                type="number"
                value={getFieldValue('faana.framework_scores.community_engagement_score')}
                onChange={(e) => handleFieldChange('faana.framework_scores.community_engagement_score', e.target.value)}
                helperText="Network strength and contribution (0-100)"
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Overall Leadership Strength Score"
                type="number"
                value={getFieldValue('faana.framework_scores.leadership_strength_score')}
                onChange={(e) => handleFieldChange('faana.framework_scores.leadership_strength_score', e.target.value)}
                helperText="Overall leadership effectiveness (0-100)"
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            </Grid>
            {/* Growth Metrics */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Growth Metrics
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Development tracking and velocity measurements
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Growth Trajectory</InputLabel>
                <Select
                  value={getFieldValue('faana.growth_metrics.growth_trajectory')}
                  onChange={(e) => handleFieldChange('faana.growth_metrics.growth_trajectory', e.target.value)}
                  label="Growth Trajectory"
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Emerging">Emerging</MenuItem>
                  <MenuItem value="Developing">Developing</MenuItem>
                  <MenuItem value="Advanced">Advanced</MenuItem>
                  <MenuItem value="Master">Master</MenuItem>
                </Select>
                <FormHelperText>Current development phase</FormHelperText>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Growth Velocity</InputLabel>
                <Select
                  value={getFieldValue('faana.growth_metrics.growth_velocity')}
                  onChange={(e) => handleFieldChange('faana.growth_metrics.growth_velocity', e.target.value)}
                  label="Growth Velocity"
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Slow">Slow</MenuItem>
                  <MenuItem value="Moderate">Moderate</MenuItem>
                  <MenuItem value="Rapid">Rapid</MenuItem>
                </Select>
                <FormHelperText>Rate of development</FormHelperText>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Assessment Date"
                type="date"
                value={getFieldValue('faana.growth_metrics.assessment_date')}
                onChange={(e) => handleFieldChange('faana.growth_metrics.assessment_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Date of last formal assessment"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Coach/Facilitator Name"
                value={getFieldValue('faana.growth_metrics.coach_name')}
                onChange={(e) => handleFieldChange('faana.growth_metrics.coach_name', e.target.value)}
                helperText="Faana-certified coach conducting assessment"
              />
            </Grid>
            {/* Risk Assessment */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Risk Assessment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Predictive analytics for growth barriers
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Growth Risk Index"
                type="number"
                value={getFieldValue('faana.risk_assessment.growth_risk_index')}
                onChange={(e) => handleFieldChange('faana.risk_assessment.growth_risk_index', e.target.value)}
                helperText="Overall risk to growth (0-10, where 10 is highest risk)"
                inputProps={{ min: 0, max: 10, step: 0.1 }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Risk Factors"
                value={getFieldValue('faana.risk_assessment.risk_factors').join ? getFieldValue('faana.risk_assessment.risk_factors').join(', ') : ''}
                onChange={(e) => handleFieldChange('faana.risk_assessment.risk_factors', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                helperText="Comma-separated list of identified growth barriers"
                placeholder="e.g., burnout risk, limited support system"
              />
            </Grid>
          </>
        );

      case 'PROJECT':
        return (
          <>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleFieldChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleFieldChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Project Lead"
                value={formData.projectLead}
                onChange={(e) => handleFieldChange('projectLead', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Budget"
                value={formData.budget}
                onChange={(e) => handleFieldChange('budget', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Objectives"
                value={formData.objectives}
                onChange={(e) => handleFieldChange('objectives', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </>
        );

      case 'PROGRAM':
        return (
          <>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Program Type"
                value={formData.programType}
                onChange={(e) => handleFieldChange('programType', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Duration"
                value={formData.duration}
                onChange={(e) => handleFieldChange('duration', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Target Audience"
                value={formData.targetAudience}
                onChange={(e) => handleFieldChange('targetAudience', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Max Participants"
                type="number"
                value={formData.maxParticipants}
                onChange={(e) => handleFieldChange('maxParticipants', e.target.value)}
              />
            </Grid>
          </>
        );

      case 'EVENT':
        return (
          <>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                value={formData.eventDate}
                onChange={(e) => handleFieldChange('eventDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Event Time"
                type="time"
                value={formData.eventTime}
                onChange={(e) => handleFieldChange('eventTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleFieldChange('location', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => handleFieldChange('capacity', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Registration URL"
                value={formData.registrationUrl}
                onChange={(e) => handleFieldChange('registrationUrl', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Agenda"
                value={formData.agenda}
                onChange={(e) => handleFieldChange('agenda', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </>
        );

      case 'RESOURCE':
        return (
          <>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Resource Type"
                value={formData.resourceType}
                onChange={(e) => handleFieldChange('resourceType', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Resource URL"
                value={formData.resourceUrl}
                onChange={(e) => handleFieldChange('resourceUrl', e.target.value)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Access Level</InputLabel>
                <Select
                  value={formData.accessLevel}
                  onChange={(e) => handleFieldChange('accessLevel', e.target.value)}
                  label="Access Level"
                >
                  <MenuItem value="PUBLIC">Public</MenuItem>
                  <MenuItem value="PRIVATE">Private</MenuItem>
                  <MenuItem value="RESTRICTED">Restricted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Version"
                value={formData.version}
                onChange={(e) => handleFieldChange('version', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Owner"
                value={formData.owner}
                onChange={(e) => handleFieldChange('owner', e.target.value)}
              />
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business color="primary" />
          Create New Entity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add a new entity to the system
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Type Selection - Always show first */}
        <Grid size={12}>
          <FormControl fullWidth required error={touchedFields.type && !!validationErrors.type}>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleFieldChange('type', e.target.value)}
              onBlur={() => handleFieldBlur('type')}
              label="Entity Type"
            >
              {ENTITY_TYPES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touchedFields.type && validationErrors.type && (
              <FormHelperText>{validationErrors.type}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Show fields only if a type is selected */}
        {formData.type && (
          <>
            {/* Common fields for all entity types */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label={formData.type === 'PERSON' ? 'Full Name' : 'Entity Name'}
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                onBlur={() => handleFieldBlur('name')}
                error={touchedFields.name && !!validationErrors.name}
                helperText={touchedFields.name && validationErrors.name}
                required
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  label="Status"
                >
                  {ENTITY_STATUS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Type-specific fields */}
            {renderTypeSpecificFields()}

            {/* Common contact fields - show for most types */}
            {['ORGANIZATION', 'PERSON', 'EVENT'].includes(formData.type) && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    onBlur={() => handleFieldBlur('email')}
                    error={touchedFields.email && !!validationErrors.email}
                    helperText={touchedFields.email && validationErrors.email}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    onBlur={() => handleFieldBlur('phone')}
                    error={touchedFields.phone && !!validationErrors.phone}
                    helperText={touchedFields.phone && validationErrors.phone}
                  />
                </Grid>
              </>
            )}

            {/* Website - for organizations and resources */}
            {['ORGANIZATION', 'RESOURCE'].includes(formData.type) && (
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={(e) => handleFieldChange('website', e.target.value)}
                  onBlur={() => handleFieldBlur('website')}
                  error={touchedFields.website && !!validationErrors.website}
                  helperText={touchedFields.website && validationErrors.website}
                />
              </Grid>
            )}

            {/* Address - for organizations and events */}
            {['ORGANIZATION', 'EVENT'].includes(formData.type) && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            {/* Description - for all types */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                multiline
                rows={4}
              />
            </Grid>
          </>
        )}

        <Grid size={12}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
            <TextField
              fullWidth
              placeholder="Add tags (press Enter)"
              onKeyPress={handleAddTag}
              size="small"
              helperText="Press Enter to add tags"
            />
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenDialog(true)}
          disabled={isLoading || !isFormValid()}
          startIcon={isLoading ? <CircularProgress size={20} /> : <Save />}
        >
          {isLoading ? 'Creating...' : 'Create Entity'}
        </Button>
      </Box>
      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Entity Creation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to create a new entity "{formData.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Notification Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default EntityCreationForm;